<template>
  <div class="microbiology-results width-responsiveness">
    <div
      v-if="loading"
      class="loading-container"
    >
      <Spinner />
      <p>Carregando resultados...</p>
    </div>

    <div
      v-else-if="results.length === 0"
      class="no-results"
    >
      <p>Nenhum resultado encontrado.</p>
    </div>

    <div v-else>
      <div class="results-header">
        <div class="filter-container">
          <div class="search">
            <input
              v-model="filterText"
              placeholder="Filtrar listagem..."
              class="search-box__input"
            />
            <img
              src="/static/images/icon-search-purple.svg"
              class="search-box__icon"
            />
          </div>
        </div>

        <h3>{{ filteredResults.length }} resultado(s) encontrado(s)</h3>
      </div>

      <DataCardTable
        :columns="columns"
        :data="filteredResults"
        :default-sort="{ key: 'patient', direction: 'asc' }"
        @sort-change="handleSortChange"
      >
        <template #cell-status="{ value }">
          <span
            class="status-badge"
            :class="getStatusClass(value)"
          >
            {{ value }}
          </span>
        </template>

        <template #expanded-content="{ item }">
          <div class="expanded-details">
            <div class="exam-info-section">
              <div class="exam-info-grid">
                <div class="info-item">
                  <span class="label">Exame | Amostra</span>
                  <span class="value">{{ item.exam }} | {{ item.sample }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Sítio</span>
                  <span class="value">{{ item.sitio }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Material</span>
                  <span class="value">{{ item.material }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Status</span>
                  <span
                    class="value status-badge"
                    :class="getStatusClass(item.status)"
                  >
                    {{ item.status }}
                  </span>
                </div>
                <div class="info-item download-section">
                  <button
                    class="btn-download"
                    @click="downloadReport(item)"
                    :disabled="downloading === item.id"
                  >
                    <md-icon v-if="downloading === item.id">hourglass_empty</md-icon>
                    <md-icon v-else>download</md-icon>
                    {{ downloading === item.id ? 'Baixando...' : 'Baixar' }}
                  </button>
                </div>
              </div>
            </div>

            <div class="timeline-section">
              <MicrobiologyProgressBar :progress-steps="item.progress" />
            </div>
          </div>
        </template>

        <template #empty-state>
          <p>Nenhum resultado encontrado.</p>
        </template>
      </DataCardTable>
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/common/Spinner'
import DataCardTable from '@/components/common/DataCardTable.vue'
import MicrobiologyProgressBar from './MicrobiologyProgressBar.vue'
import microbiologyApi from '@/api/microbiology'
import notify from '@/api/notification'
import { downloadFileByByteArray } from '@/utils/convertByteArrayToFile'

export default {
  name: 'MicrobiologyResults',
  components: {
    Spinner,
    DataCardTable,
    MicrobiologyProgressBar,
  },
  props: {
    results: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      downloading: null,
      filterText: '',
      filteredResults: [],
      columns: [
        {
          key: 'orderId',
          title: 'OS ALVARO',
          sortable: true,
          width: '12%',
        },
        {
          key: 'orderIdLis',
          title: 'OS LIS',
          sortable: true,
          width: '12%',
        },
        {
          key: 'patient',
          title: 'PACIENTE',
          sortable: true,
          flex: 2,
        },
        {
          key: 'collectionDate',
          title: 'DATA DA COLETA',
          sortable: true,
          width: '18%',
        },
        {
          key: 'requestDate',
          title: 'DATA DA SOLICITAÇÃO',
          sortable: true,
          width: '18%',
        },
      ],
    }
  },
  watch: {
    results: {
      immediate: true,
      handler(newResults) {
        this.filteredResults = [...newResults]
        this.filterText = ''
      },
    },
    filterText() {
      this.filterResults()
    },
  },
  methods: {
    handleSortChange(sortConfig) {
      this.$emit('sort-change', sortConfig)
    },

    filterResults() {
      if (!this.filterText || !this.filterText.trim()) {
        this.filteredResults = [...this.results]
        return
      }

      const searchTerm = this.filterText.toLowerCase().trim()
      this.filteredResults = this.results.filter(result => {
        const patient = String(result.patient || '').toLowerCase()
        const orderId = String(result.orderId || '').toLowerCase()
        const orderIdLis = String(result.orderIdLis || '').toLowerCase()

        return (
          patient.includes(searchTerm) ||
          orderId.includes(searchTerm) ||
          orderIdLis.includes(searchTerm)
        )
      })
    },

    getStatusClass(status) {
      switch (status) {
        case 'Finalizado':
          return 'status-completed'
        case 'Em andamento':
          return 'status-progress'
        case 'Final Positivo':
          return 'status-positive'
        default:
          return 'status-default'
      }
    },

    async downloadReport(result) {
      if (this.downloading === result.id) return

      this.downloading = result.id

      try {
        const response = await microbiologyApi.downloadMicrobiologyReport(result.orderId)

        const fileName = `laudo_microbiologia_${result.orderId}_${result.examCode}.pdf`
        downloadFileByByteArray(response.data, fileName, 'application/pdf')

        notify({
          title: 'SUCESSO:',
          message: 'Laudo baixado com sucesso!',
        })
      } catch (error) {
        console.error('Erro ao baixar laudo:', error)
        notify({
          title: 'ERRO:',
          message: 'Erro ao baixar o laudo. Tente novamente.',
        })
      } finally {
        this.downloading = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.microbiology-results {
  margin-top: 30px;
  font-family: 'Exo 2', sans-serif !important;

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px;
    gap: 16px;

    p {
      color: #666;
      font-size: 14px;
    }
  }

  .no-results {
    text-align: center;
    padding: 40px;
    color: #666;
  }

  .results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h3 {
      color: #363636;
      font-size: 16px;
      font-weight: 600;
      margin: 0;
    }
  }

  .filter-container {
    .search {
      background-color: #fff;
      border-radius: 5px;
      border: 1px solid #e0e0e0;
      display: flex;
      align-items: center;
      padding: 8px 10px;

      .search-box__input {
        border: none;
        outline: none;
        min-width: 220px;
        font-family: 'Exo 2', sans-serif;
        font-size: 14px;
        color: #363636;
        background: transparent;

        &::placeholder {
          color: #7a7a7a;
        }
      }

      .search-box__icon {
        width: 16px;
        height: 16px;
        margin-left: 8px;
        flex-shrink: 0;
      }
    }
  }

  .status-badge {
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;

    &.status-completed {
      background: #e6f7ff;
      color: #1890ff;
      border: 1px solid #91d5ff;
    }

    &.status-progress {
      background: #fff7e6;
      color: #fa8c16;
      border: 1px solid #ffd591;
    }

    &.status-positive {
      background: #ffe6ee;
      color: #b40048;
      border: 1px solid #f5c5d7;
    }

    &.status-default {
      background: #f5f5f5;
      color: #999;
      border: 1px solid #d9d9d9;
    }
  }

  .expanded-details {
    .exam-info-section {
      margin-bottom: 20px;

      .exam-info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 16px;
        align-items: start;

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .label {
            font-size: 11px;
            color: #999;
            font-weight: 500;
            text-transform: uppercase;
          }

          .value {
            font-size: 14px;
            color: #363636;
            font-weight: 500;
          }

          &.download-section {
            justify-content: center;
            align-items: center;
          }
        }
      }
    }

    .btn-download {
      background-color: #7f1a54;
      color: white;
      border: none;
      border-radius: 4px;
      padding: 8px 16px;
      font-size: 12px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      transition: background-color 0.2s ease;
      font-family: 'Exo 2', sans-serif;

      &:hover {
        background-color: #9e1e68;
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .md-icon {
        font-size: 16px;
      }
    }
  }
}

@media (max-width: 768px) {
  .microbiology-results {
    .expanded-details .exam-info-section .exam-info-grid {
      grid-template-columns: 1fr;
      gap: 12px;
    }
  }
}
</style>
